// Renders hitbox elements from the KEYS array. Provides a debug overlay
// (?debug=1 in the URL) that draws outlines around every hitbox so positions
// can be visually iterated.
//
// LCD_BOUNDS is no longer hardcoded here — it lives in overlay-meta.json
// (loaded by main.ts via src/overlay.ts).

import { KEYS, type KeyDef } from './keymap';

const DEBUG = typeof location !== 'undefined' && location.search.includes('debug=1');
const pressedOverlayKeys = new Set<string>();

export function initLayout(): void {
  const container = document.querySelector<HTMLElement>('.hitboxes');
  if (!container) return;

  container.innerHTML = '';
  container.appendChild(makeBorderFillPatch());

  for (const key of KEYS) {
    if (key.renderHitbox === false) continue;
    container.appendChild(makeHitbox(key));
  }

  if (DEBUG) {
    document.body.classList.add('debug-hitboxes');
  }
}

function makeBorderFillPatch(): HTMLDivElement {
  const el = document.createElement('div');
  el.className = 'border-fill-patch border-fill-patch--cyan-copy';
  el.setAttribute('aria-hidden', 'true');
  return el;
}

function makeHitbox(key: KeyDef): HTMLButtonElement {
  const el = document.createElement('button');
  el.type = 'button';
  el.className = `hitbox hitbox--${key.shape ?? 'round'}`;
  el.dataset.key = key.id;
  if (key.overlay) el.dataset.overlay = key.overlay;
  if (key.label) el.setAttribute('aria-label', key.label);
  el.style.top = key.position.top;
  el.style.left = key.position.left;
  el.style.width = key.position.width;
  el.style.height = key.position.height;
  if (key.polygon) {
    // Traced visible surfaces should win over older rectangular fallback
    // zones when they overlap, especially the cropped shared D-pad surface.
    el.style.zIndex = '1';
    el.style.clipPath = polygonToCss(key.polygon);
  }
  return el;
}

function polygonToCss(points: NonNullable<KeyDef['polygon']>): string {
  return `polygon(${points.map((point) => `${point.x} ${point.y}`).join(', ')})`;
}

/**
 * Toggle the press-state overlay for a key. Used by main.ts on
 * pointerdown / keydown — paired with a clearKeyOverlay() on release.
 */
export function setKeyOverlay(keyId: string, pressed: boolean): void {
  const key = KEYS.find((k) => k.id === keyId);
  if (!key || !key.overlay) return;
  if (pressed) {
    pressedOverlayKeys.add(key.id);
  } else {
    pressedOverlayKeys.delete(key.id);
  }

  const overlayPressed = KEYS.some((k) => {
    return k.overlay === key.overlay && pressedOverlayKeys.has(k.id);
  });

  const img = document.querySelector<HTMLImageElement>(
    `.device-press[data-overlay="${key.overlay}"]`,
  );
  if (img) img.classList.toggle('active', overlayPressed);

  // Also highlight every hitbox that maps to the same physical overlay. The
  // cropped D-pad only visibly shows one rubber surface, but keyboard
  // left/right/down still need to make that visible surface look pressed.
  const hits = document.querySelectorAll<HTMLElement>(
    `.hitbox[data-overlay="${key.overlay}"]`,
  );
  for (const hit of hits) {
    hit.classList.toggle('is-pressed', overlayPressed);
  }
}
